import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import copy from "copy-to-clipboard";
import moment from "moment";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";
import {
  escapeTicketMessageHtml,
  openTicketImageInNewTab,
  parseTicketMessage,
} from "../../../utils/ticketMessageUtils";
import "./myTickets.css";

const TAB_IDS = ["all", "open", "pending", "closed"];
const TICKET_CHAT_MODAL_ID = "agce_my_ticket_chat_modal";

function TicketMessageContent({ text, onPreviewImage }) {
  const segments = useMemo(() => parseTicketMessage(text), [text]);

  if (!segments.length) return null;

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === "image") {
          return (
            <button
              key={`img-${index}`}
              type="button"
              className="agce-tchat__img-link"
              onClick={() => onPreviewImage(segment.url)}
              aria-label="Open image preview"
            >
              <img
                src={segment.url}
                alt="attachment"
                className="agce-tchat__inline-img"
                loading="lazy"
              />
            </button>
          );
        }

        return (
          <span
            key={`text-${index}`}
            dangerouslySetInnerHTML={{ __html: escapeTicketMessageHtml(segment.text) }}
          />
        );
      })}
    </>
  );
}

function extractTicketsList(res) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (d && typeof d === "object" && Array.isArray(d.data)) return d.data;
  return [];
}

function tabIdForStatus(status) {
  const x = String(status || "").trim().toLowerCase();
  if (x.includes("pending") || x.includes("progress")) return "pending";
  if (x.includes("closed") || x.includes("resolved")) return "closed";
  if (x.includes("open")) return "open";
  return "open";
}

function isReplyAllowed(status) {
  const x = String(status || "").trim().toLowerCase();
  return x === "open" || x === "pending" || x.includes("progress");
}

function formatCategoryDisplay(category) {
  if (category == null || String(category).trim() === "") return "—";
  return String(category)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCreatedOn(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function formatDateTime(iso) {
  if (!iso) return "";
  const m = moment(iso);
  if (!m.isValid()) return String(iso);
  return m.format("DD/MM/YYYY hh:mm A");
}

function fileToCompressedDataUrl(file, maxSide = 1024, quality = 0.7) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file"));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Invalid image"));
      img.onload = () => {
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (!w || !h) {
          reject(new Error("Could not read image dimensions"));
          return;
        }
        if (w > maxSide || h > maxSide) {
          const ratio = Math.min(maxSide / w, maxSide / h);
          w = Math.max(1, Math.round(w * ratio));
          h = Math.max(1, Math.round(h * ratio));
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas not supported"));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        try {
          const out = canvas.toDataURL("image/jpeg", quality);
          resolve(out);
        } catch (err) {
          reject(err);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function mapApiTicketToRow(t, index) {
  const description = t?.description != null ? String(t.description) : "";
  return {
    _key: t?._id || t?.ticketId || `ticket-${index}`,
    ticketId: t?.ticketId != null ? String(t.ticketId) : "",
    type: formatCategoryDisplay(t?.category),
    description,
    status: t?.status != null ? String(t.status) : "—",
    created: formatCreatedOn(t?.createdAt),
    _tabId: tabIdForStatus(t?.status),
    _raw: t || {},
  };
}

function rowMatchesQuery(row, q) {
  if (!q) return true;
  const hay = `${row.ticketId} ${row.type} ${row.description} ${row.status} ${row.created}`.toLowerCase();
  return hay.includes(q);
}

export default function MyTicketsPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [query, setQuery] = useState("");
  const [allRows, setAllRows] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedPreview, setAttachedPreview] = useState("");
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { userDetails } = useContext(ProfileContext) || {};

  const loadTickets = useCallback(
    async ({ silent = false, selectKey = null } = {}) => {
      if (DISABLE_ALL_NETWORK) {
        setAllRows([]);
        return [];
      }
      if (!silent) LoaderHelper.loaderStatus(true);
      try {
        const res = await AuthService.getUserTickets();
        const ok = res?.success === true || res?.success === 1;
        if (!ok) {
          alertErrorMessage(res?.message || "Could not load tickets.");
          setAllRows([]);
          return [];
        }
        const list = extractTicketsList(res);
        const reversed = Array.isArray(list) ? [...list].reverse() : [];
        const mapped = reversed.map((t, i) => mapApiTicketToRow(t, i));
        setAllRows(mapped);

        if (selectKey) {
          const match = mapped.find((r) => {
            const raw = r._raw || {};
            return (
              raw._id === selectKey ||
              raw.id === selectKey ||
              r.ticketId === selectKey ||
              r._key === selectKey
            );
          });
          if (match) setSelectedTicket(match);
        }
        return mapped;
      } finally {
        if (!silent) LoaderHelper.loaderStatus(false);
      }
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mapped = await loadTickets();
      if (cancelled) return;
      void mapped;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadTickets]);

  const tabRows = useMemo(
    () => ({
      all: allRows,
      open: allRows.filter((r) => r._tabId === "open"),
      pending: allRows.filter((r) => r._tabId === "pending"),
      closed: allRows.filter((r) => r._tabId === "closed"),
    }),
    [allRows],
  );

  const tabCounts = useMemo(() => {
    const c = { all: allRows.length, open: 0, pending: 0, closed: 0 };
    allRows.forEach((r) => {
      if (r._tabId === "open") c.open += 1;
      else if (r._tabId === "pending") c.pending += 1;
      else if (r._tabId === "closed") c.closed += 1;
    });
    return c;
  }, [allRows]);

  const rows = useMemo(() => {
    const base = tabRows[activeTab] || allRows;
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) => rowMatchesQuery(r, q));
  }, [activeTab, query, tabRows, allRows]);

  const clearAttachment = useCallback(() => {
    setAttachedFile(null);
    setAttachedPreview((prev) => {
      if (prev) {
        try {
          URL.revokeObjectURL(prev);
        } catch {
          /* noop */
        }
      }
      return "";
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const openTicketChat = useCallback(
    (row) => {
      if (!row) return;
      setSelectedTicket(row);
      setChatInput("");
      clearAttachment();
      if (typeof window === "undefined") return;
      const el = document.getElementById(TICKET_CHAT_MODAL_ID);
      if (!el) return;
      const bs = window.bootstrap;
      if (bs && bs.Modal) {
        const inst = bs.Modal.getOrCreateInstance(el);
        inst.show();
      }
      setTimeout(() => {
        messagesEndRef?.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      }, 300);
    },
    [clearAttachment],
  );

  useEffect(() => {
    return () => {
      if (attachedPreview) {
        try {
          URL.revokeObjectURL(attachedPreview);
        } catch {
          /* noop */
        }
      }
    };
  }, [attachedPreview]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const el = document.getElementById(TICKET_CHAT_MODAL_ID);
    if (!el) return undefined;
    const onHide = () => {
      clearAttachment();
      setChatInput("");
    };
    el.addEventListener("hidden.bs.modal", onHide);
    return () => el.removeEventListener("hidden.bs.modal", onHide);
  }, [clearAttachment]);

  const handlePickAttachment = useCallback(() => {
    if (sending) return;
    fileInputRef.current?.click();
  }, [sending]);

  const handleAttachmentChange = useCallback(
    (event) => {
      event.preventDefault();
      const file = event.target.files?.[0];
      if (!file) return;
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      const maxSize = 5 * 1024 * 1024;
      if (!allowedTypes.includes(file.type)) {
        alertErrorMessage("Only PNG, JPEG, and JPG file types are allowed.");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (file.size > maxSize) {
        alertErrorMessage("Max image size is 5MB.");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      let url = "";
      try {
        url = URL.createObjectURL(file);
      } catch {
        url = "";
      }
      setAttachedFile(file);
      setAttachedPreview((prev) => {
        if (prev) {
          try {
            URL.revokeObjectURL(prev);
          } catch {
            /* noop */
          }
        }
        return url;
      });
    },
    [],
  );

  const replies = useMemo(() => {
    if (!selectedTicket) return [];
    const raw = selectedTicket._raw || {};
    return Array.isArray(raw.ticket) ? raw.ticket : [];
  }, [selectedTicket]);

  useEffect(() => {
    if (replies.length > 0) {
      messagesEndRef?.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [replies]);

  const handleSendChat = useCallback(async () => {
    if (sending) return;
    if (!selectedTicket) return;
    const msg = chatInput.trim();
    if (!msg && !attachedFile) {
      alertErrorMessage("Please enter a message or attach an image");
      return;
    }
    if (DISABLE_ALL_NETWORK) {
      alertErrorMessage("Network is disabled.");
      return;
    }
    const raw = selectedTicket._raw || {};
    const ticketKey = raw._id || raw.id || raw.ticketId || selectedTicket.ticketId;
    if (!ticketKey) {
      alertErrorMessage("Invalid ticket");
      return;
    }
    try {
      setSending(true);
      LoaderHelper.loaderStatus(true);

      let payloadMsg = msg;
      if (attachedFile) {
        try {
          const dataUrl = await fileToCompressedDataUrl(attachedFile, 1024, 0.7);
          const imgPart = `[img]${dataUrl}[/img]`;
          payloadMsg = msg ? `${msg}\n${imgPart}` : imgPart;
        } catch (compressErr) {
          alertErrorMessage(compressErr?.message || "Could not process image");
          return;
        }
      }

      const res = await AuthService.replyTicket(payloadMsg, ticketKey);
      const ok = res?.success === true || res?.success === 1;
      if (!ok) {
        alertErrorMessage(res?.message || res?.msg || "Failed to send message");
        return;
      }
      setChatInput("");
      clearAttachment();
      await loadTickets({ silent: true, selectKey: ticketKey });
      alertSuccessMessage("Message sent successfully");
    } catch (err) {
      alertErrorMessage(err?.message || "An error occurred while sending message");
    } finally {
      setSending(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [chatInput, attachedFile, selectedTicket, sending, loadTickets, clearAttachment]);

  const onChatKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendChat();
      }
    },
    [handleSendChat],
  );

  const handleCopyTicketId = useCallback((id) => {
    if (!id) return;
    copy(String(id));
    alertSuccessMessage("Ticket ID copied!");
  }, []);

  const handlePreviewImage = useCallback((src) => {
    if (!src) return;
    setPreviewImageSrc(src);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewImageSrc("");
  }, []);

  const handleOpenPreviewInNewTab = useCallback(() => {
    if (!previewImageSrc) return;
    openTicketImageInNewTab(previewImageSrc);
  }, [previewImageSrc]);

  useEffect(() => {
    if (!previewImageSrc || typeof document === "undefined") return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") handleClosePreview();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [previewImageSrc, handleClosePreview]);

  const handleRefreshMessages = useCallback(async () => {
    if (!selectedTicket || refreshing) return;
    const raw = selectedTicket._raw || {};
    const ticketKey = raw._id || raw.id || raw.ticketId || selectedTicket.ticketId;
    try {
      setRefreshing(true);
      await loadTickets({ silent: true, selectKey: ticketKey });
    } catch {
      alertErrorMessage("Unable to refresh messages");
    } finally {
      setRefreshing(false);
    }
  }, [selectedTicket, refreshing, loadTickets]);

  const getUserInitial = useCallback(() => {
    if (userDetails?.firstName) return String(userDetails.firstName).charAt(0).toUpperCase();
    if (userDetails?.emailId) return String(userDetails.emailId).charAt(0).toUpperCase();
    return "U";
  }, [userDetails]);

  const sel = selectedTicket;
  const selRaw = sel?._raw || {};
  const selSubject = selRaw?.subject || selRaw?.title || sel?.description || "";
  const selPriority = selRaw?.priority ? String(selRaw.priority) : "";
  const selDescription = sel?.description || "";
  const selCategoryDisplay = sel?.type || "";
  const selCreatedFull = formatDateTime(selRaw?.createdAt);
  const selStatus = sel?.status || "";
  const canReply = isReplyAllowed(selStatus);
  const userInitial = getUserInitial();

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section Overview_mid">
        <div className="kyc_approval_s agce-mtickets-wrap">
          <div className="cnt agce-mtickets">
            <div className="agce-mtickets__head">
              <div className="agce-mtickets__titles">
                <h1 className="agce-mtickets__title">My Tickets</h1>
                <p className="agce-mtickets__sub">
                  Track and manage all your support requests in one place.
                </p>
              </div>
              <button
                type="button"
                className="agce-mtickets__cta"
                onClick={() => navigate("/user_profile/create_tickets")}
              >
                New ticket
              </button>
            </div>

            <div className="agce-mtickets__toolbar">
              <div className="agce-mtickets__tabs" role="tablist" aria-label="Ticket filters">
                {TAB_IDS.map((id) => {
                  const label =
                    id === "all" ? "All" : id === "open" ? "Open" : id === "pending" ? "Pending" : "Closed";
                  const count = tabCounts[id];
                  return (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === id}
                      className={`agce-mtickets__tab ${activeTab === id ? "agce-mtickets__tab--active" : ""}`}
                      onClick={() => setActiveTab(id)}
                    >
                      {id === "all" ? "All" : `${label}(${count})`}
                    </button>
                  );
                })}
              </div>
              <div className="agce-mtickets__search">
                <div className="agce-mtickets__search-wrap">
                  <i className="ri-search-line" aria-hidden="true" />
                  <input
                    type="search"
                    className="agce-mtickets__search-input"
                    placeholder="Search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label="Search tickets"
                  />
                </div>
              </div>
            </div>

            <div className="agce-mtickets__tablewrap">
              <table className="agce-mtickets__table">
                <thead>
                  <tr>
                    <th scope="col">Ticket number</th>
                    <th className="col right-alien">Type</th>
                    <th className="col right-alien">Description</th>
                    <th scope="col">Status</th>
                    <th scope="col">Created on</th>
                    <th scope="col" className="agce-mtickets__th-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="agce-mtickets__empty">
                        <div className="agce-mtickets__emptyBox">
                          <img
                            src="/images/no_data_vector.svg"
                            className="img-fluid dark_img"
                            width="96"
                            height="96"
                            alt="No data"
                          />
                          <img
                            src="/images/no_data_vector_light.png"
                            className="img-fluid light_img"
                            width="96"
                            height="96"
                            alt="No data"
                          />
                          {/* <div className="agce-mtickets__emptyText">No tickets found</div> */}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row._key || `${activeTab}-${row.ticketId}-${row.created}`}>
                        <td className="agce-mtickets__td-ticket" data-label="Ticket number">#{row.ticketId}</td>
                        <td className="col right-alien" data-label="Type">{row.type}</td>
                        <td className="col right-alien" data-label="Description">{row.description}</td>
                        <td data-label="Status">{row.status}</td>
                        <td data-label="Created on">{row.created}</td>
                        <td className="agce-mtickets__td-action" data-label="Action">
                          <button
                            type="button"
                            className="agce-mtickets__link"
                            onClick={() => openTicketChat(row)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade agce-tchat"
        id={TICKET_CHAT_MODAL_ID}
        tabIndex="-1"
        aria-labelledby="agceMyTicketChatModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered agce-tchat__dialog">
          <div className="modal-content agce-tchat__content">
            <button
              type="button"
              className="agce-tchat__close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="ri-close-line" aria-hidden="true" />
            </button>

            <div className="agce-tchat__head">
              <h3 className="agce-tchat__title" id="agceMyTicketChatModalLabel">
                Help / Support
                <button
                  type="button"
                  className="agce-tchat__refresh"
                  onClick={handleRefreshMessages}
                  disabled={!sel || refreshing}
                  aria-label={refreshing ? "Refreshing messages" : "Refresh messages"}
                  title="Refresh"
                >
                  <i
                    className={refreshing ? "ri-loader-4-line agce-tchat__spin" : "ri-refresh-line"}
                    aria-hidden="true"
                  />
                </button>
              </h3>
              {sel ? (
                <p className="agce-tchat__sub">
                  Ticket{" "}
                  <span
                    className="agce-tchat__tid"
                    role="button"
                    tabIndex={0}
                    title="Copy ticket ID"
                    onClick={() => handleCopyTicketId(sel.ticketId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCopyTicketId(sel.ticketId);
                      }
                    }}
                  >
                    #{sel.ticketId} <i className="ri-file-copy-line" aria-hidden="true" />
                  </span>
                </p>
              ) : (
                <p className="agce-tchat__sub">View and reply to your support ticket.</p>
              )}
            </div>

            <div className="agce-tchat__body">
              {sel ? (
                <>
                  <div className="agce-tchat__info">
                    <div className="agce-tchat__info-row">
                      <span className="agce-tchat__info-label">Created on</span>
                      <span className="agce-tchat__info-val">
                        {selCreatedFull || sel.created || "N/A"}
                      </span>
                    </div>
                    {selSubject ? (
                      <div className="agce-tchat__info-row">
                        <span className="agce-tchat__info-label">Subject</span>
                        <span className="agce-tchat__info-val">{selSubject}</span>
                      </div>
                    ) : null}
                    <div className="agce-tchat__chips">
                      {selCategoryDisplay ? (
                        <span className="agce-tchat__chip">{selCategoryDisplay}</span>
                      ) : null}
                      {selPriority ? (
                        <span className="agce-tchat__chip agce-tchat__chip--priority">
                          {selPriority}
                        </span>
                      ) : null}
                      {selStatus ? (
                        <span className="agce-tchat__chip agce-tchat__chip--status">
                          {selStatus}
                        </span>
                      ) : null}
                    </div>
                    {selDescription ? (
                      <p className="agce-tchat__desc">{selDescription}</p>
                    ) : null}
                  </div>

                  <div className="agce-tchat__messages">
                    {replies.length === 0 ? (
                      <div
                        className={`agce-tchat__msg agce-tchat__msg--them`}
                      >
                        <div className="agce-tchat__avatar">T</div>
                        <div className="agce-tchat__bubble">
                          <div className="agce-tchat__bubble-text">
                            No messages yet. Our support team will respond shortly.
                          </div>
                        </div>
                      </div>
                    ) : (
                      replies.map((item, i) => {
                        const mine = item?.replyBy !== 0;
                        return (
                          <div
                            key={item?._id || `${i}-${item?.createdAt || ""}`}
                            className={`agce-tchat__msg ${mine ? "agce-tchat__msg--mine" : "agce-tchat__msg--them"}`}
                          >
                            {mine ? null : <div className="agce-tchat__avatar">T</div>}
                            <div className="agce-tchat__bubble">
                              <div className="agce-tchat__bubble-text">
                                <TicketMessageContent
                                  text={item?.query || ""}
                                  onPreviewImage={handlePreviewImage}
                                />
                              </div>
                              {item?.createdAt ? (
                                <div className="agce-tchat__bubble-time">
                                  {formatDateTime(item.createdAt)}
                                </div>
                              ) : null}
                            </div>
                            {mine ? (
                              <div className="agce-tchat__avatar agce-tchat__avatar--mine">
                                {userInitial}
                              </div>
                            ) : null}
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </>
              ) : null}
            </div>

            <div className="agce-tchat__foot">
              {canReply && attachedFile ? (
                <div className="agce-tchat__attach-preview">
                  {attachedPreview ? (
                    <button
                      type="button"
                      className="agce-tchat__attach-thumb-btn"
                      onClick={() => handlePreviewImage(attachedPreview)}
                      aria-label="Preview attached image"
                    >
                      <img
                        src={attachedPreview}
                        alt={attachedFile.name}
                        className="agce-tchat__attach-thumb"
                      />
                    </button>
                  ) : (
                    <span className="agce-tchat__attach-thumb agce-tchat__attach-thumb--icon">
                      <i className="ri-image-line" aria-hidden="true" />
                    </span>
                  )}
                  <div className="agce-tchat__attach-meta">
                    <div className="agce-tchat__attach-name" title={attachedFile.name}>
                      {attachedFile.name}
                    </div>
                    <div className="agce-tchat__attach-size">
                      {(attachedFile.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                  <button
                    type="button"
                    className="agce-tchat__attach-remove"
                    onClick={clearAttachment}
                    disabled={sending}
                    aria-label="Remove attachment"
                    title="Remove attachment"
                  >
                    <i className="ri-close-line" aria-hidden="true" />
                  </button>
                </div>
              ) : null}

              <div className="agce-tchat__input-wrap">
                {canReply ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                      style={{ display: "none" }}
                      onChange={handleAttachmentChange}
                    />
                    <button
                      type="button"
                      className="agce-tchat__attach"
                      onClick={handlePickAttachment}
                      disabled={!sel || sending}
                      aria-label="Attach image"
                      title="Attach image"
                    >
                      <i className="ri-attachment-2" aria-hidden="true" />
                    </button>
                    <input
                      type="text"
                      className="agce-tchat__input"
                      placeholder="Write your message here..."
                      maxLength={1000}
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={onChatKeyDown}
                      disabled={!sel || sending}
                    />
                    <button
                      type="button"
                      className="agce-tchat__send"
                      onClick={handleSendChat}
                      disabled={!sel || sending || (!chatInput.trim() && !attachedFile)}
                      aria-label={sending ? "Sending message" : "Send message"}
                      title={sending ? "Sending..." : "Send"}
                    >
                      {sending ? (
                        <i className="ri-loader-4-line agce-tchat__spin" aria-hidden="true" />
                      ) : (
                        <i className="ri-send-plane-2-fill" aria-hidden="true" />
                      )}
                    </button>
                  </>
                ) : (
                  <input
                    type="text"
                    className="agce-tchat__input"
                    placeholder="This ticket has been resolved"
                    value=""
                    disabled
                    readOnly
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {previewImageSrc && typeof document !== "undefined"
        ? createPortal(
            <div
              className="agce-tchat__preview-backdrop"
              role="dialog"
              aria-modal="true"
              aria-label="Image preview"
              onClick={handleClosePreview}
            >
              <div
                className="agce-tchat__preview-panel"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="agce-tchat__preview-close"
                  onClick={handleClosePreview}
                  aria-label="Close preview"
                >
                  <i className="ri-close-line" aria-hidden="true" />
                </button>
                <img
                  src={previewImageSrc}
                  alt="Attachment preview"
                  className="agce-tchat__preview-img"
                />
                <button
                  type="button"
                  className="agce-tchat__preview-open"
                  onClick={handleOpenPreviewInNewTab}
                >
                  Open in new tab
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
